import { Col, Row } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../api/api';
import Navbar from '../../components/Navbar/Navbar';
import NavbarFooter from '../../components/Navbar/NavbarFooter';
import HeaderStrip from '../../components/header-strip/HeaderStrip';
import Router from '../../router/Router';
import { authActions } from '../../store/auth';
import { dataActions } from '../../store/data';
import { deleteLocalData, errorHandler, getAccessToken, getLocalData, setAxiosHeaders } from '../../utils/helper';
import ChooseEntity from './ChooseEntity/ChooseEntity';
import { multiEntityActions } from '../../store/multiEntity';
import useBrokerConfig from '../../hooks/useBrokerConfig';
import LoadingComponent from '../../components/UI/Loading/LoadingComponent';
import { MOBILE_MAX_WIDTH } from '../../constants/breakpoints';
import { useEvenAuthStatus } from '../../hooks/useEvenRedirect';
import { hasEvenProduct } from '../../components/UI/QuickLinks/quickLinksConfig';

interface Props {
  loading: boolean;
}

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);

    // Call handleResize to set initial size
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return windowSize;
};

const DashboardLayout: React.FC<Props> = ({ loading }) => {
  const dispatch = useDispatch(),
    [loader, setLoader] = useState<boolean>(false),
    accessToken = getAccessToken(),
    { member_uuid, organisation_uuid, activeMode, roles } = useSelector((state: any) => state.auth),
    { chosenEntity, entities, canChooseEntity } = useSelector((state: any) => state.multiEntity),
    { width } = useWindowSize(),
    [orgProductLoading, setOrgProductLoading] = useState(false),
    { isCommercialOnly, userDetails, hasEvenQuicklnks } = useSelector((state: any) => state.data),
    { fetchBrokerConfig } = useBrokerConfig(),
    initialChosenEntityRef = useRef<string | null>(null),
    activeModeRef = useRef<string>(activeMode),
    bannersRequestRef = useRef<{ controller?: AbortController; orgUuid?: string; mode?: string }>({}),
    getAvailableOrganisations = (signal?: AbortSignal) => {
      setAxiosHeaders(accessToken);
      api.multiEntityAccess
        .getMultiEntityAccess(undefined, signal)
        .then((res) => {
          if (signal?.aborted) return;
          let sortedOrganisations = res?.organisations;
          try {
            sortedOrganisations = res?.organisations?.sort(
              (a: any, b: any) => Number(b?.is_default || false) - Number(a?.is_default || false)
            );
          } catch (error) {
            sortedOrganisations = res?.organisations;
          }
          dispatch(
            multiEntityActions.setEntities({
              entities: sortedOrganisations,
              canChooseEntity: res?.can_select_organisation,
              totalAdminCount: res?.total_admin_count,
            })
          );
          dispatch(authActions.setName(res?.name));
          dispatch(authActions.setRoles({ roles: res?.role }));
          if (!res?.can_select_organisation || width <= MOBILE_MAX_WIDTH) {
            const org = sortedOrganisations[0]?.organisation_uuid;
            fetchBrokerConfig(org);
            getUserDetails(org);
            getChoices(org);
            getBanners(org);
            if (res?.role?.includes('employer')) {
              getOrgProducts(org);
            }
            dispatch(multiEntityActions.setChosenEntity(sortedOrganisations[0]));
          }
        })
        .catch((err) => {
          if (signal?.aborted) return;
          errorHandler(err?.response?.data);
        });
    },
    getUserDetails = (entity_uuid?: string) => {
      api.profile
        .userData()
        .then((response) => {
          dispatch(
            authActions.userData({
              id: response?.id,
              uuid: response?.uuid,
              member_id: response?.member_id,
              member_uuid: response?.member_uuid,
              status: response?.status,
              roles: response?.roles,
              roles_name: response?.roles_name,
              name: response?.full_name || response?.first_name,
              organisation_id: response?.organisation_id,
              organisation_uuid: response?.organisation_uuid || entity_uuid,
              relationship_managers: response?.relationship_manager,
              // uhi: { is_uhi_available: response?.is_uhi_available },
              email: response?.email,
              phone: response?.phone,
            })
          );
          getMemberDetails(entity_uuid || response?.organisation_uuid, response?.member_uuid);
          // dispatch(authActions.setUHI({ uhi: { is_uhi_available: response?.is_uhi_available } }));
        })
        .catch((error) => {});
    },
    getChoices = (entity_uuid?: string) => {
      api.products
        .selectChoices({ organisation_id: entity_uuid ?? organisation_uuid })
        .then((response) => {
          dispatch(dataActions.setChoices({ choices: response?.data }));
        })
        .catch((err: any) => {
          errorHandler(err?.response?.data);
        });
    },
    getOrgProducts = (entity_uuid?: string) => {
      setOrgProductLoading(true);
      api.products
        .getProducts({ organisation_uuid: entity_uuid ?? organisation_uuid })
        .then((response) => {
          dispatch(dataActions.setOrgProducts({ orgProducts: response?.[0] }));
          if (response?.[0]?.policies?.length + response?.[0]?.subscriptions?.length === 0) {
            dispatch(authActions.setRoles({ roles: ['employee'] }));
          }
          setOrgProductLoading(false);
        })
        .catch((error) => {
          setOrgProductLoading(false);
          errorHandler(error?.response?.data || 'Something went wrong');
        });
    },
    getMemberDetails = (entity_id?: string, user_uuid?: string) => {
      const orgUuid = entity_id || chosenEntity?.organisation_uuid || organisation_uuid;
      if (!orgUuid) return;
      api.employee
        .getEmployeeDetails({
          organisation_uuid: orgUuid,
          member_uuid: user_uuid ?? member_uuid,
        })
        .then((response: any) => {
          const userDetails = response?.data?.[0] || response?.data || response;
          dispatch(dataActions.setUserDetails({ userDetails }));
          if (userDetails?.roles_name || userDetails?.roles) {
            dispatch(
              authActions.setRoles({
                roles: userDetails?.roles,
                roles_name: userDetails?.roles_name,
              })
            );
          }
        })
        .catch((error) => {
          errorHandler(error?.response?.data);
        });
    },
    // The employee endpoint only returns EMPLOYEE/BOTH banners, so HR/admin mode reads from the employer endpoint.
    getBanners = (entity_uuid?: string, mode: string = activeModeRef.current) => {
      const orgUuid = entity_uuid ?? organisation_uuid;
      if (!orgUuid) return;
      bannersRequestRef.current.controller?.abort();
      const controller = new AbortController();
      bannersRequestRef.current = { controller, orgUuid, mode };
      const request =
        mode === 'employer'
          ? api.members.getEmployerBanners(orgUuid, controller.signal)
          : api.employee.getBanners(orgUuid, controller.signal);
      request
        .then((response: any) => {
          const banners = Array.isArray(response) ? response : response?.data || [];
          dispatch(dataActions.setBanners({ banners }));
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.error('Error fetching banners:', error);
        });
    },
    switchToken = (org: string) => {
      setLoader(true);
      api.multiEntityAccess
        .switchToken({ organisation_uuid: org })
        .then((response) => {
          if (response.access_token) {
            dispatch(authActions.login({ accessToken: response.access_token, refreshToken: response.refresh_token }));
            setAxiosHeaders(response.access_token);
          }
          let mode = activeModeRef.current;
          if (!chosenEntity?.is_default && roles?.includes('employer')) {
            dispatch(authActions.changeActiveMode('employer'));
            mode = 'employer';
          }
          dispatch(authActions.setOrganisationUuid(org));
          fetchBrokerConfig(org);
          getUserDetails(org);
          getChoices(org);
          getOrgProducts(org);
          getBanners(org, mode);
        })
        .catch((error) => {
          errorHandler(error?.response?.data);
        })
        .finally(() => {
          setLoader(false);
        });
    };

  // Once member details and the org config are in and the user is eligible for Even, fetch the Even token in the
  // background so Even pages open without waiting on it.
  useEvenAuthStatus({ enabled: hasEvenQuicklnks === true && hasEvenProduct({ userDetails }) });

  useEffect(() => {
    if (accessToken?.length > 0 && entities?.length === 0) {
      const controller = new AbortController();
      getAvailableOrganisations(controller.signal);
      return () => controller.abort();
    }
  }, [accessToken, entities]);

  useEffect(() => {
    return () => {
      if (getLocalData('upgradetoken')) {
        deleteLocalData('upgradetoken');
      }
    };
  }, []);

  // Employee and HR modes read banners from different endpoints, so refetch for the same org when the mode flips.
  useEffect(() => {
    activeModeRef.current = activeMode;
    const { orgUuid, mode } = bannersRequestRef.current;
    if (orgUuid && mode !== activeMode) {
      getBanners(orgUuid, activeMode);
    }
  }, [activeMode]);

  useEffect(() => {
    if (isCommercialOnly && activeMode === 'employee' && roles?.includes('employer')) {
      dispatch(authActions.changeActiveMode('employer'));
    }
  }, [isCommercialOnly, activeMode, roles]);

  useEffect(() => {
    if (!chosenEntity?.organisation_uuid || !canChooseEntity) return;
    if (initialChosenEntityRef.current === chosenEntity.organisation_uuid) return;
    if (width <= MOBILE_MAX_WIDTH && initialChosenEntityRef.current === null) {
      initialChosenEntityRef.current = chosenEntity.organisation_uuid;
      return;
    }
    initialChosenEntityRef.current = chosenEntity.organisation_uuid;
    switchToken(chosenEntity.organisation_uuid);
  }, [chosenEntity?.organisation_uuid, canChooseEntity]);

  if (!chosenEntity && width > MOBILE_MAX_WIDTH) {
    return <ChooseEntity />;
  }

  return (
    <LoadingComponent loading={loader || orgProductLoading || loading}>
      <HeaderStrip />
      <Row className="container">
        <Col className="navbar-wrapper">
          <Navbar />
        </Col>
        <Col className="router">
          <Row>
            <Col span={24} className="router-component">
              <Router />
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="show-on-mobile">
        <NavbarFooter />
      </div>
    </LoadingComponent>
  );
};

export default DashboardLayout;
